import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  byId: {},
  allIds: [],
  bootstrapped: false,
  perProjectRates: {},
  perProjectFetched: {},
  loading: false,
  error: null,
};

const upsertEntry = (state, lc) => {
  if (!lc || !lc._id) return;
  const existing = state.byId[lc._id];
  state.byId[lc._id] = {
    _id: lc._id,
    name: lc.name,
    isDeleted: lc.isDeleted ?? existing?.isDeleted ?? false,
  };
  if (!state.allIds.includes(lc._id)) state.allIds.push(lc._id);
};

const slice = createSlice({
  name: 'laborClassifications',
  initialState,
  reducers: {
    setLoading(state, { payload }) {
      state.loading = !!payload;
    },
    setError(state, { payload }) {
      state.error = payload ?? null;
    },
    bootstrapSucceeded(state, { payload }) {
      state.byId = {};
      state.allIds = [];
      (payload || []).forEach((lc) => upsertEntry(state, lc));
      state.bootstrapped = true;
      state.loading = false;
      state.error = null;
    },
    projectListMerged(state, { payload }) {
      const { projectId, list } = payload;
      const nextRates = {};
      (list || []).forEach((lc) => {
        // Soft-deleted entries are typically not returned without includeDeleted=true;
        // preserve any soft-deleted entries already in byId so name resolution survives.
        upsertEntry(state, lc);
        if (lc.rate) nextRates[lc._id] = { ...lc.rate };
      });
      state.perProjectRates[projectId] = nextRates;
      state.perProjectFetched[projectId] = true;
    },
    lcCreated(state, { payload }) {
      upsertEntry(state, payload);
    },
    lcRenamed(state, { payload }) {
      if (!payload || !payload._id) return;
      const existing = state.byId[payload._id];
      if (existing) existing.name = payload.name;
      else upsertEntry(state, payload);
    },
    lcSoftDeleted(state, { payload }) {
      const id = typeof payload === 'string' ? payload : payload?._id;
      if (!id) return;
      if (state.byId[id]) {
        state.byId[id].isDeleted = true;
      } else if (payload?.name) {
        // keep it so historical references still resolve the name, just flagged deleted.
        upsertEntry(state, { ...payload, isDeleted: true });
      }
      Object.keys(state.perProjectRates).forEach((projectId) => {
        if (state.perProjectRates[projectId]?.[id]) {
          delete state.perProjectRates[projectId][id];
        }
      });
    },
    projectRatesReplaced(state, { payload }) {
      // BE returns the authoritative full set on every project save. We key
      // by the URL projectId (or, for create, the new project's _id) — the
      // `projectId` field on each rate row is opaque and may point at the
      // original project for submission clones.
      const { projectId, rates } = payload;
      if (!projectId) return;
      const next = {};
      (rates || []).forEach((rate) => {
        if (!rate || !rate.laborClassificationId) return;
        const slot = {};
        if (rate.earningsCode !== undefined) slot.earningsCode = rate.earningsCode;
        if (rate.baseRate !== undefined) slot.baseRate = rate.baseRate;
        if (rate.fringeBenefit !== undefined) slot.fringeBenefit = rate.fringeBenefit;
        next[rate.laborClassificationId] = slot;
      });
      state.perProjectRates[projectId] = next;
      state.perProjectFetched[projectId] = true;
    },
    reset() {
      return initialState;
    },
  },
});

export const laborClassificationsActions = slice.actions;

export default slice.reducer;
